import * as THREE from "three/webgpu";
import type { CartographyLabelTier } from "../../domain/mesh";

const MAX_TEXTURE_DPR = 3;

export type LabelHierarchy = "primary" | "secondary" | "singularity";

const labelTheme = {
  background: "rgba(5, 8, 15, 0.76)",
  backgroundHero: "rgba(4, 6, 12, 0.82)",
  borderPrimary: "rgba(232, 239, 252, 0.28)",
  borderSecondary: "rgba(178, 190, 210, 0.13)",
  borderHero: "rgba(236, 243, 255, 0.3)",
  primaryName: "rgba(248, 251, 255, 0.96)",
  primaryMeta: "rgba(202, 214, 233, 0.74)",
  secondaryName: "rgba(188, 198, 216, 0.7)",
  secondaryMeta: "rgba(140, 153, 176, 0.52)",
  orbitalDot: "rgba(166, 182, 207, 0.56)",
} as const;

export type TextureSpec = {
  texture: THREE.CanvasTexture;
  widthPx: number;
  heightPx: number;
};

export function createNodeLabelTexture(
  name: string,
  meta: string,
  tier: CartographyLabelTier,
  hierarchy: LabelHierarchy,
): TextureSpec {
  const metrics = nodeTypography(tier, hierarchy);
  return drawLabelTexture(
    name,
    meta,
    tier,
    metrics.nameSize,
    metrics.metaSize,
    metrics.paddingX,
    metrics.paddingY,
    hierarchy,
    metrics.tracking,
  );
}

export function createOrbitalLabelTexture(label: string, eyebrow: string): TextureSpec {
  const dpr = textureDpr();
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Canvas 2D context unavailable for scene label texture");

  const logicalHeight = 28;
  const nameFont = `600 9.5px ${fontFamily()}`;
  const metaFont = `500 7.5px ${fontFamily()}`;
  const nameWidth = trackedTextWidth(context, label, nameFont, 0.45);
  const metaWidth = trackedTextWidth(context, eyebrow, metaFont, 0.35);
  const logicalWidth = Math.ceil(Math.max(nameWidth + 32, metaWidth + 32, 100));

  canvas.width = Math.ceil(logicalWidth * dpr);
  canvas.height = Math.ceil(logicalHeight * dpr);
  context.scale(dpr, dpr);
  roundedRect(context, 0.5, 0.5, logicalWidth - 1, logicalHeight - 1, 8);
  context.fillStyle = labelTheme.background;
  context.fill();
  context.strokeStyle = labelTheme.borderSecondary;
  context.lineWidth = 1;
  context.stroke();

  context.fillStyle = labelTheme.orbitalDot;
  context.beginPath();
  context.arc(10.5, logicalHeight / 2, 2.1, 0, Math.PI * 2);
  context.fill();
  context.textBaseline = "middle";
  context.fillStyle = labelTheme.secondaryName;
  drawTrackedText(context, label, 19, 9.8, nameFont, 0.45, "left");
  context.fillStyle = labelTheme.secondaryMeta;
  drawTrackedText(context, eyebrow, 19, 19.7, metaFont, 0.35, "left");

  return { texture: canvasTexture(canvas), widthPx: logicalWidth, heightPx: logicalHeight };
}

function drawLabelTexture(
  name: string,
  meta: string,
  tier: CartographyLabelTier,
  nameSize: number,
  metaSize: number,
  paddingX: number,
  paddingY: number,
  hierarchy: LabelHierarchy,
  tracking: number,
): TextureSpec {
  const dpr = textureDpr();
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Canvas 2D context unavailable for scene label texture");

  const nameFont = `650 ${nameSize}px ${fontFamily()}`;
  const metaFont = `520 ${metaSize}px ${fontFamily()}`;
  const nameWidth = trackedTextWidth(context, name, nameFont, tracking);
  const metaWidth = trackedTextWidth(context, meta, metaFont, tracking * 0.84);
  const logicalWidth = Math.ceil(Math.max(nameWidth, metaWidth) + paddingX * 2);
  const lineGap = tier === "hero" ? 6 : 4;
  const logicalHeight = Math.ceil(paddingY * 2 + nameSize + metaSize + lineGap);

  canvas.width = Math.ceil(logicalWidth * dpr);
  canvas.height = Math.ceil(logicalHeight * dpr);
  context.scale(dpr, dpr);
  roundedRect(context, 0.5, 0.5, logicalWidth - 1, logicalHeight - 1, tier === "hero" ? 11 : 9);
  context.fillStyle = hierarchy === "singularity" ? labelTheme.backgroundHero : labelTheme.background;
  context.fill();
  context.strokeStyle =
    hierarchy === "singularity"
      ? labelTheme.borderHero
      : hierarchy === "primary"
        ? labelTheme.borderPrimary
        : labelTheme.borderSecondary;
  context.lineWidth = 1;
  context.stroke();

  context.textBaseline = "middle";
  context.fillStyle = hierarchy === "secondary" ? labelTheme.secondaryName : labelTheme.primaryName;
  const nameY = paddingY + nameSize * 0.5;
  drawTrackedText(context, name, logicalWidth / 2, nameY, nameFont, tracking, "center");
  context.fillStyle = hierarchy === "secondary" ? labelTheme.secondaryMeta : labelTheme.primaryMeta;
  const metaY = nameY + nameSize * 0.5 + lineGap + metaSize * 0.5;
  drawTrackedText(context, meta, logicalWidth / 2, metaY, metaFont, tracking * 0.84, "center");

  return { texture: canvasTexture(canvas), widthPx: logicalWidth, heightPx: logicalHeight };
}

function nodeTypography(tier: CartographyLabelTier, hierarchy: LabelHierarchy) {
  const secondaryScale = hierarchy === "secondary" ? 0.88 : 1;
  if (tier === "hero")
    return { nameSize: 16 * secondaryScale, metaSize: 9 * secondaryScale, paddingX: 18, paddingY: 10, tracking: 0.72 };
  if (tier === "major")
    return { nameSize: 13 * secondaryScale, metaSize: 8 * secondaryScale, paddingX: 14, paddingY: 8, tracking: 0.58 };
  return { nameSize: 10.5 * secondaryScale, metaSize: 7.5 * secondaryScale, paddingX: 12, paddingY: 7, tracking: 0.42 };
}

function trackedTextWidth(context: CanvasRenderingContext2D, text: string, font: string, tracking: number) {
  context.font = font;
  return context.measureText(text).width + Math.max(text.length - 1, 0) * tracking;
}

function drawTrackedText(
  context: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  font: string,
  tracking: number,
  align: "left" | "center",
) {
  context.font = font;
  const width = trackedTextWidth(context, text, font, tracking);
  let cursor = align === "center" ? x - width * 0.5 : x;
  for (const character of text) {
    context.fillText(character, cursor, y);
    cursor += context.measureText(character).width + tracking;
  }
}

function canvasTexture(canvas: HTMLCanvasElement) {
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.generateMipmaps = true;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.needsUpdate = true;
  return texture;
}

function roundedRect(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
) {
  const r = Math.min(radius, width * 0.5, height * 0.5);
  context.beginPath();
  context.moveTo(x + r, y);
  context.arcTo(x + width, y, x + width, y + height, r);
  context.arcTo(x + width, y + height, x, y + height, r);
  context.arcTo(x, y + height, x, y, r);
  context.arcTo(x, y, x + width, y, r);
  context.closePath();
}

function textureDpr() {
  return Math.min(window.devicePixelRatio || 1, MAX_TEXTURE_DPR);
}
function fontFamily() {
  return 'Inter, ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
}
